import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, ConfirmDialog } from '@mlm/ui';
import { api } from '../lib/api';

type Period = {
  id: string;
  code: string;
  startAt: string;
  endAt: string;
  status: 'open' | 'closed';
  closedAt: string | null;
};

export function PeriodsPage() {
  const qc = useQueryClient();
  const [closing, setClosing] = useState<{ id: string; code: string } | null>(null);
  const [closeError, setCloseError] = useState<string | null>(null);

  const listQ = useQuery({
    queryKey: ['periods'],
    queryFn: async () => (await api.get<{ periods: Period[] }>('/periods')).data.periods,
  });

  const currentQ = useQuery({
    queryKey: ['periods-current'],
    queryFn: async () => (await api.get<Period>('/periods/current')).data,
  });

  const closeMut = useMutation({
    meta: { successMessage: 'Period closed.' },
    mutationFn: async (id: string) => {
      await api.post(`/periods/${id}/close`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['periods'] });
      qc.invalidateQueries({ queryKey: ['periods-current'] });
      setClosing(null);
    },
  });

  async function handleConfirmClose() {
    if (!closing) return;
    setCloseError(null);
    try {
      await closeMut.mutateAsync(closing.id);
    } catch (err) {
      setCloseError(err instanceof Error ? err.message : 'Failed to close period.');
    }
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Current Period</CardTitle>
        </CardHeader>
        <CardContent>
          {currentQ.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : currentQ.data ? (
            <div className="flex items-center justify-between">
              <div className="text-sm">
                <p className="font-mono text-base">{currentQ.data.code}</p>
                <p className="text-xs text-muted-foreground">
                  {new Date(currentQ.data.startAt).toLocaleDateString()} →{' '}
                  {new Date(currentQ.data.endAt).toLocaleDateString()}
                </p>
                <p className="text-xs text-muted-foreground">Status: {currentQ.data.status}</p>
              </div>
              {currentQ.data.status === 'open' ? (
                <Button
                  variant="outline"
                  disabled={closeMut.isPending}
                  onClick={() => {
                    setCloseError(null);
                    setClosing({ id: currentQ.data!.id, code: currentQ.data!.code });
                  }}
                >
                  {closeMut.isPending ? 'Closing…' : 'Close period'}
                </Button>
              ) : null}
            </div>
          ) : null}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={closing !== null}
        onOpenChange={(next) => {
          if (!next) {
            setClosing(null);
            setCloseError(null);
          }
        }}
        title={closing ? `Close period ${closing.code}` : 'Close period'}
        description={
          <span>
            This fires the Stairstep payout event and writes <code>slot_rank</code> rows.
            Cannot be undone without admin SQL.
          </span>
        }
        confirmLabel="Close period"
        confirmVariant="destructive"
        loading={closeMut.isPending}
        errorMessage={closeError}
        onConfirm={handleConfirmClose}
        typedConfirm={
          closing
            ? { expected: `CLOSE ${closing.code}` }
            : undefined
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>All Periods</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {listQ.isLoading ? (
            <p className="p-6 text-sm text-muted-foreground">Loading…</p>
          ) : !listQ.data?.length ? (
            <p className="p-6 text-sm text-muted-foreground">No periods yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground border-b">
                <tr>
                  <th className="px-4 py-2 font-medium">Code</th>
                  <th className="px-4 py-2 font-medium">Start</th>
                  <th className="px-4 py-2 font-medium">End</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Closed at</th>
                </tr>
              </thead>
              <tbody>
                {listQ.data.map((p) => (
                  <tr key={p.id} className="border-b last:border-0">
                    <td className="px-4 py-2 font-mono">{p.code}</td>
                    <td className="px-4 py-2 text-xs">{new Date(p.startAt).toLocaleDateString()}</td>
                    <td className="px-4 py-2 text-xs">{new Date(p.endAt).toLocaleDateString()}</td>
                    <td className="px-4 py-2">
                      <Badge variant={p.status === 'open' ? 'success' : 'muted'}>{p.status}</Badge>
                    </td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">
                      {p.closedAt ? new Date(p.closedAt).toLocaleString() : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
